import { createContext, useState, useEffect, useContext } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { baseURL } from '../utils/config';

const swal = require('sweetalert2');

const AuthContext = createContext();

export default AuthContext;

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [authTokens, setAuthTokens] = useState(() => {
    return localStorage.getItem("authTokens")
      ? JSON.parse(localStorage.getItem("authTokens"))
      : null;
  });

  const [user, setUser] = useState(() => {
    return authTokens ? jwtDecode(authTokens.access) : null;
  });

  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // ─── Connexion ────────────────────────────────────────────────────────
  const loginUser = async (username, password) => {
    const response = await fetch(`${baseURL}/token/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password })
    });

    const data = await response.json();

    if (response.status === 200) {
      const decodedUser = jwtDecode(data.access);

      if (!decodedUser.is_super_admin && (!decodedUser.permissions || decodedUser.permissions.length === 0)) {
        swal.fire({
          title: "Vous n'avez aucune permission pour accéder à l'application",
          icon: "error",
          toast: true,
          timer: 3000,
          position: 'top-right',
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return;
      }

      setAuthTokens(data);
      setUser(jwtDecode(data.access));
      localStorage.setItem("authTokens", JSON.stringify(data));
      navigate("/");

      swal.fire({
        title: "Connexion réussie",
        icon: "success",
        toast: true,
        timer: 2000,
        position: 'top-right',
        timerProgressBar: true,
        showConfirmButton: false,
      });

    } else {
      swal.fire({
        title: "Nom d'utilisateur ou mot de passe incorrect",
        icon: "error",
        toast: true,
        timer: 2000,
        position: 'top-right',
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  // ─── Inscription ──────────────────────────────────────────────────────
  const registerUser = async (email, username, password, password2) => {
    const response = await fetch(`${baseURL}/register/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, username, password, password2 })
    });

    if (response.status === 201) {
      navigate("/login");
      swal.fire({
        title: "Inscription réussie, connectez-vous maintenant",
        icon: "success",
        toast: true,
        timer: 2000,
        position: 'top-right',
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } else {
      swal.fire({
        title: `Une erreur s'est produite ${response.status}`,
        icon: "error",
        toast: true,
        timer: 2000,
        position: 'top-right',
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  // ─── Déconnexion ──────────────────────────────────────────────────────
  // silent = true → pas de toast (le swal est déjà affiché avant l'appel)
  const logoutUser = (silent = false) => {
    setAuthTokens(null);
    setUser(null);
    localStorage.removeItem("authTokens");
    navigate("/login");

    if (!silent) {
      swal.fire({
        title: "Vous avez été déconnecté.",
        icon: "success",
        toast: true,
        timer: 2000,
        position: 'top-right',
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  // ─── Rechargement des infos utilisateur depuis l'API ──────────────────
  // Gardée pour d'autres composants éventuels — fusionne les données
  // pour ne pas écraser les champs JWT (permissions, is_super_admin, etc.)
  const updateUserInfo = async () => {
    if (!authTokens) return;

    try {
      const response = await fetch(`${baseURL}/mon-compte/`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${authTokens.access}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Fusion : champs JWT préservés + champs profil mis à jour
        setUser(prevUser => ({
          ...prevUser,
          ...data,
        }));
      } else {
        console.error("Erreur lors du rechargement des infos utilisateur");
      }
    } catch (error) {
      console.error("Erreur réseau pour updateUserInfo :", error);
    }
  };

  // ─── Sync des tokens au montage et à chaque changement ────────────────
  useEffect(() => {
    if (authTokens) {
      setUser(jwtDecode(authTokens.access));
    }
    setLoading(false);
  }, [authTokens]);

  // ─── Contexte exposé ──────────────────────────────────────────────────
  const contextData = {
    user,
    setUser,
    authTokens,
    setAuthTokens,
    registerUser,
    loginUser,
    logoutUser,
    updateUserInfo,
  };

  return (
    <AuthContext.Provider value={contextData}>
      {loading ? null : children}
    </AuthContext.Provider>
  );
};